import { useNavigate, useOutletContext } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '../../lib/supabase'
import { ArrowRight } from 'lucide-react'
import type { SearchFilters } from '../search/AdvancedSearch'

interface IssueItemContribution {
  id: string
  title: string
  brand: string | null
  cached_image_url: string | null
  images: Array<{ url: string; cached_url?: string; highlighted?: boolean }> | null
  item_classifications: Array<{ category_id: string | null; subcategory_id: string | null }> | { category_id: string | null; subcategory_id: string | null } | null
  item_tags: Array<{ tag_id: string; tags: { name: string } | null }> | { tag_id: string; tags: { name: string } | null } | null
}

interface Issue {
  id: string
  title: string
  narrative?: string
  slug: string
  status: string
  featured: boolean
  published_at?: string
  tags?: string[]
  curator?: {
    first_name: string
    last_name: string
    avatar_url?: string
    username?: string
    is_guest?: boolean
    editorial_profile?: boolean
    curator_kicker?: string | null
  }
  highlighted_image_url?: string
  _count?: {
    items: number
  }
  preview_image?: string
  issue_items?: Array<{ contribution: IssueItemContribution | null }>
}

// A curation matches if at least one item satisfies ALL active structured filters,
// and separately, the text query matches curation-level OR item-level fields
function issueMatchesFilters(issue: Issue, filters: SearchFilters): boolean {
  const q = filters.query?.toLowerCase()

  // Text search: split into terms so "footwear sneakers" matches across fields
  if (q) {
    const terms = q.split(/\s+/).filter(Boolean)
    const allTermsMatch = terms.every(term => {
      const curationMatch = issue.title?.toLowerCase().includes(term) ||
                            issue.narrative?.toLowerCase().includes(term)
      const itemMatch = issue.issue_items?.some(item =>
        item.contribution?.title?.toLowerCase().includes(term) ||
        item.contribution?.brand?.toLowerCase().includes(term)
      )
      return curationMatch || itemMatch
    })
    if (!allTermsMatch) return false
  }

  // Structured filters: at least one item must satisfy ALL active criteria
  const hasStructured = filters.categories.length > 0 || filters.subcategories.length > 0 ||
                        filters.tags.length > 0 || filters.brands.length > 0
  if (!hasStructured) return true

  return issue.issue_items?.some(item => {
    const c = item.contribution
    if (!c) return false
    // Supabase may return a single object instead of an array for deeply nested embeds
    const rawCl = c.item_classifications
    const classifications = Array.isArray(rawCl) ? rawCl : rawCl ? [rawCl] : []
    const rawTags = c.item_tags
    const tags = Array.isArray(rawTags) ? rawTags : rawTags ? [rawTags] : []
    if (filters.categories.length > 0 && !classifications.some(cl => cl.category_id && filters.categories.includes(cl.category_id))) return false
    if (filters.subcategories.length > 0 && !classifications.some(cl => cl.subcategory_id && filters.subcategories.includes(cl.subcategory_id))) return false
    if (filters.tags.length > 0 && !tags.some(t => t.tags?.name && filters.tags.includes(t.tags.name))) return false
    if (filters.brands.length > 0 && !filters.brands.includes(c.brand || '')) return false
    return true
  }) ?? false
}

export function CuratedIssues() {
  const navigate = useNavigate()
  const context = useOutletContext<{ activeFilters?: SearchFilters | null; onClearFilters?: () => void }>()
  const activeFilters = context?.activeFilters || null
  const onClearFilters = context?.onClearFilters

  const getDisplayImageUrl = (contribution: any): string | undefined => {
    const highlightedImage = contribution?.images?.find((img: any) => img.highlighted)
    const firstImage = contribution?.images?.[0]
    return (
      highlightedImage?.cached_url ||
      firstImage?.cached_url ||
      contribution?.cached_image_url ||
      highlightedImage?.url ||
      firstImage?.url
    )
  }

  // The default feed only needs a cover image + item count + curator, so it
  // fetches a slim per-item shape (id + image). The heavy per-item joins
  // (classifications, tags, title, brand) exist ONLY to satisfy client-side
  // filtering, so we pull them lazily — only when a filter/search is active.
  const CURATOR_SELECT = `curator:curator_id(
            first_name, last_name, avatar_url, username,
            is_guest, editorial_profile, curator_kicker
          )`
  const LIGHT_SELECT = `
          *,
          ${CURATOR_SELECT},
          issue_items!inner(
            position,
            contribution:contributions(id, cached_image_url, images)
          )
        `
  const FULL_SELECT = `
          *,
          ${CURATOR_SELECT},
          issue_items!inner(
            position,
            contribution:contributions(
              id,
              title,
              brand,
              cached_image_url,
              images,
              item_classifications(category_id, subcategory_id),
              item_tags(tag_id, tags(name))
            )
          )
        `

  // Fetch published curations from any curator (status='published' only;
  // admins control published vs private). Cached via React Query so returning
  // to /curations serves from cache instead of refetching.
  const fetchIssues = async (full: boolean): Promise<Issue[]> => {
      const { data: issuesData, error } = await supabase
        .from('issues')
        .select(full ? FULL_SELECT : LIGHT_SELECT)
        .eq('status', 'published')
        .order('published_at', { ascending: false })

      if (error) throw error

      const processedIssues = (issuesData as any[])?.map((issue: any) => {
        const items = [...(issue.issue_items || [])].sort(
          (a: any, b: any) => (a.position ?? 0) - (b.position ?? 0)
        )
        const firstItemWithImage = items.find((item: any) =>
          item.contribution && getDisplayImageUrl(item.contribution)
        )
        const previewImage = firstItemWithImage
          ? getDisplayImageUrl(firstItemWithImage.contribution)
          : undefined

        return {
          ...issue,
          preview_image: previewImage,
          _count: {
            items: items.length
          }
        }
      }) || []

      return processedIssues
  }

  // Filtering needs the heavy per-item data; the default view does not. Fetch
  // (and cache) each shape separately so activating a filter pulls the richer
  // payload on demand, and clearing it returns instantly to the light cache.
  const filtersActive = !!activeFilters
  const { data: issues = [], isLoading } = useQuery<Issue[]>({
    queryKey: ['curations-feed', filtersActive ? 'full' : 'light'],
    queryFn: () => fetchIssues(filtersActive),
  })

  if (isLoading) {
    // The banner is static (no data needed) so it paints immediately, and the
    // grid shows skeleton cards shaped like the real 4:5 covers — the page
    // reveals its structure right away instead of a bare centered spinner.
    return (
      <div>
        <div className="bg-[#57241F] text-white">
          <div className="container mx-auto px-4 py-6 md:py-8 max-w-4xl text-center">
            <p className="text-sm md:text-base uppercase tracking-widest opacity-90" style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}>
              Curated by
            </p>
            <h2 className="text-2xl md:text-3xl mt-2" style={{ fontFamily: 'Canela, Georgia, serif', fontWeight: 300 }}>
              The Collective
            </h2>
          </div>
        </div>
        <style>{`
          .cur-container { max-width: 1320px; margin: 0 auto; padding: 44px 44px 90px; }
          .cur-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 56px 40px; }
          @media (max-width: 900px) { .cur-grid { grid-template-columns: repeat(2, 1fr); gap: 48px 28px; } }
          @media (max-width: 640px) { .cur-container { padding: 28px 20px 64px; } }
          @media (max-width: 560px) { .cur-grid { grid-template-columns: 1fr; gap: 40px; } }
        `}</style>
        <div className="cur-container">
          <div className="cur-grid animate-pulse">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i}>
                <div style={{ aspectRatio: '4 / 5', borderRadius: 2, backgroundColor: '#E5E0D6' }} />
                <div className="pt-3.5">
                  <div style={{ height: 20, width: '68%', backgroundColor: '#E5E0D6', borderRadius: 2 }} />
                  <div className="mt-2.5" style={{ height: 13, width: '92%', backgroundColor: '#ECE7DA', borderRadius: 2 }} />
                  <div className="mt-1.5" style={{ height: 13, width: '54%', backgroundColor: '#ECE7DA', borderRadius: 2 }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  // Apply filters client-side
  const isFiltering = !!activeFilters
  const displayIssues = isFiltering ? issues.filter((issue: Issue) => issueMatchesFilters(issue, activeFilters)) : issues

  if (issues.length === 0) {
    return (
      <div className="text-center py-20">
        <h2 className="text-2xl font-semibold mb-3">No Curated Issues Yet</h2>
        <p className="text-gray-600">Check back soon for curated collections.</p>
      </div>
    )
  }

  if (displayIssues.length === 0 && isFiltering) {
    return (
      <div className="text-center py-20">
        <h2 className="text-2xl font-semibold mb-3" style={{ fontFamily: "Canela, Georgia, serif" }}>
          No curations match your search
        </h2>
        <p className="text-gray-600 mb-6">Try adjusting your filters or clearing them to see all curations.</p>
        {onClearFilters && (
          <button
            onClick={onClearFilters}
            className="px-6 py-2 bg-black text-white rounded-full hover:bg-gray-800 transition-colors"
            style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}
          >
            Clear filters
          </button>
        )}
      </div>
    )
  }

  return (
    <div>
      {/* Section Banner */}
      <div className="bg-[#57241F] text-white">
        <div className="container mx-auto px-4 py-6 md:py-8 max-w-4xl text-center">
          <p
            className="text-sm md:text-base uppercase tracking-widest opacity-90"
            style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}
          >
            Curated by
          </p>
          <h2
            className="text-2xl md:text-3xl mt-2"
            style={{ fontFamily: 'Canela, Georgia, serif', fontWeight: 300 }}
          >
            The Collective
          </h2>
        </div>
      </div>

      {/* Aligned grid (discovery index). Masonry is reserved for INSIDE a single
          curation, where varied item heights add rhythm — not used here. */}
      <style>{`
        .cur-container { max-width: 1320px; margin: 0 auto; padding: 44px 44px 90px; }
        .cur-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 56px 40px; }
        @media (max-width: 900px) { .cur-grid { grid-template-columns: repeat(2, 1fr); gap: 48px 28px; } }
        @media (max-width: 640px) { .cur-container { padding: 28px 20px 64px; } }
        @media (max-width: 560px) { .cur-grid { grid-template-columns: 1fr; gap: 40px; } }
      `}</style>
      <div className="cur-container">
        <div className="cur-grid">
          {displayIssues.map((issue: Issue, i: number) => {
            // Editorial curators (guests, admins, editorial founders) get an
            // avatar + kicker + name credit over the curation's own cover. The
            // kicker resolves the same way as the editorial hero.
            const c = issue.curator
            const isEditorial = !!c?.editorial_profile
            const kickerLabel = c?.curator_kicker?.trim() || (c?.is_guest ? 'Guest Curator' : 'Featured Curator')
            return (
            <div
              key={issue.id}
              className="group cursor-pointer"
              onClick={() => navigate(
                `/profile/${issue.curator?.username || 'purposecollective'}/issues/${issue.slug}`,
                // Hand the detail view what we already know so its header/cover
                // paint instantly while the items load (seed-then-revalidate).
                { state: { curationSeed: {
                  id: issue.id,
                  title: issue.title,
                  narrative: issue.narrative,
                  slug: issue.slug,
                  curator_id: (issue as any).curator_id,
                  curator: issue.curator,
                  preview_image: issue.preview_image,
                  highlighted_image_url: issue.highlighted_image_url,
                } } }
              )}
            >
              {/* Cover — fixed 4:5 so every row aligns and titles share a baseline */}
              <div className="relative" style={{ aspectRatio: '4 / 5', borderRadius: 2, overflow: 'hidden', backgroundColor: '#E5E0D6' }}>
                {issue.preview_image ? (
                  <>
                    <img
                      src={issue.preview_image}
                      alt={issue.title}
                      loading={i > 2 ? 'lazy' : undefined}
                      className="w-full h-full object-cover block"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-500 pointer-events-none" />
                    {isEditorial && (
                      <>
                        <div className="absolute inset-x-0 bottom-0 pointer-events-none" style={{ height: '46%', background: 'linear-gradient(to top, rgba(0,0,0,0.64), rgba(0,0,0,0))' }} />
                        <div className="absolute left-4 bottom-4 flex items-center gap-3" style={{ maxWidth: '84%' }}>
                          {c?.avatar_url ? (
                            <img src={c.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover ring-2 ring-white/70 flex-shrink-0" />
                          ) : (
                            <div className="w-12 h-12 rounded-full flex items-center justify-center ring-2 ring-white/70 flex-shrink-0" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                              <span className="text-white text-sm font-semibold">{c?.first_name?.charAt(0)}{c?.last_name?.charAt(0)}</span>
                            </div>
                          )}
                          <div className="min-w-0" style={{ lineHeight: 1.2 }}>
                            <p className="truncate" style={{ fontFamily: "'GT America Mono', 'Courier New', monospace", fontSize: 11.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.85)' }}>{kickerLabel}</p>
                            <p className="truncate" style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: 17, fontWeight: 500, color: '#fff', marginTop: 2 }}>{c?.first_name} {c?.last_name}</p>
                          </div>
                        </div>
                      </>
                    )}
                    <div className="absolute bottom-4 right-4 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                      <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg">
                        <ArrowRight size={16} />
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400">
                    <span className="text-sm">No cover</span>
                  </div>
                )}
              </div>

              {/* Text — guest identity now lives on the cover; house cards show the curator in the meta */}
              <div className="pt-3.5">
                <h3 style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontWeight: 500, fontSize: 21, lineHeight: 1.2, color: '#2b2621' }}>
                  {issue.title}
                </h3>
                {issue.narrative && (
                  <p
                    className="mt-1.5"
                    style={{
                      fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
                      fontSize: 14.5,
                      lineHeight: 1.45,
                      color: '#6b5f52',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical' as const,
                      overflow: 'hidden',
                    }}
                  >
                    {issue.narrative}
                  </p>
                )}
                <div className="flex items-center gap-2 mt-2.5" style={{ fontFamily: "'GT America Mono', 'Courier New', monospace", fontSize: 12, color: '#9a9082' }}>
                  {isEditorial ? (
                    <span>{issue._count?.items || 0} items</span>
                  ) : (
                    <>
                      {issue.curator?.avatar_url ? (
                        <img src={issue.curator.avatar_url} alt="" className="w-4 h-4 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <span className="rounded-full flex-shrink-0" style={{ width: 6, height: 6, backgroundColor: '#c4b8a6' }} />
                      )}
                      <span className="truncate">
                        {issue.curator ? `${issue.curator.first_name} ${issue.curator.last_name}` : 'Purpose Collective'} · {issue._count?.items || 0} items
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}